import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Star, MapPin, Plus, ShoppingBag, ArrowLeft, MessageCircle, PencilLine } from "lucide-react";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose,
} from "@/components/ui/drawer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function VendorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, count } = useCart();
  const [enqOpen, setEnqOpen] = useState(false);

  const { data: vendor, isLoading } = useQuery({
    queryKey: ["vendor", id],
    queryFn: async () => (await api.get(`/vendors/${id}`)).data,
  });

  if (isLoading || !vendor)
    return <div className="p-8 text-muted-foreground">Loading vendor...</div>;

  return (
    <div className="pb-24">
      {/* Cover */}
      <div className="relative h-60 md:h-80">
        {vendor.cover ? (
          <img src={imgUrl(vendor.cover)} alt={vendor.name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-primary/30 to-secondary/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30" />
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 h-10 w-10 grid place-items-center glass rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="absolute bottom-4 left-4 right-4 md:left-8">
          <div className="flex gap-2 mb-2">
            {vendor.featured && (
              <span className="bg-secondary text-secondary-foreground text-[10px] font-bold px-2 py-1 rounded-full overline">
                Featured
              </span>
            )}
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white">
            {vendor.name}
          </h1>
          <div className="flex items-center gap-4 text-white/85 text-sm mt-1">
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-secondary text-secondary" /> {vendor.rating}
              <span className="text-white/60">({vendor.reviews})</span>
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" /> {vendor.city}
            </span>
            <span>{vendor.price_range}</span>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-6">
        <p className="text-muted-foreground">{vendor.description}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {(vendor.tags || []).map((t) => (
            <span key={t} className="text-xs font-semibold bg-muted px-3 py-1.5 rounded-full">
              {t}
            </span>
          ))}
        </div>

        <div className="mt-4">
          <EnquiryDialog vendor={vendor} open={enqOpen} setOpen={setEnqOpen} />
        </div>

        {/* Services */}
        <h2 className="font-display font-bold text-2xl tracking-tight mt-8 mb-4">
          Services
        </h2>
        <div className="grid gap-3">
          {vendor.services.map((s) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.id}`}
              className="flex items-center gap-4 bg-card border border-stone-200 rounded-2xl p-3 hover:shadow-md transition-shadow"
            >
              <img
                src={imgUrl(s.image)}
                alt={s.name}
                className="h-20 w-20 md:h-24 md:w-24 rounded-xl object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <span className="overline text-primary">{s.type}</span>
                <h3 className="font-display font-bold text-base leading-tight">{s.name}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{s.desc}</p>
                <p className="font-bold mt-1">
                  {inr(s.price)} <span className="text-xs text-muted-foreground font-normal">{s.unit}</span>
                </p>
              </div>
              <button
                data-testid={`add-service-${s.id}`}
                onClick={() => addItem(s, vendor)}
                className="shrink-0 h-10 w-10 md:w-auto md:px-4 grid place-items-center md:flex md:items-center md:gap-1.5 bg-primary text-white rounded-full font-semibold text-sm hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-5 w-5" /> <span className="hidden md:inline">Add</span>
              </button>
            </div>
          ))}
        </div>

        {(vendor.gallery || []).length > 0 && (
          <div className="mt-8" data-testid="vendor-gallery-public">
            <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Past events</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {vendor.gallery.map((src, i) => (
                <img key={i} src={imgUrl(src)} alt={`${vendor.name} event ${i + 1}`} data-testid={`gallery-img-${i}`} className="w-full aspect-square object-cover rounded-2xl border border-stone-200" loading="lazy" />
              ))}
            </div>
          </div>
        )}

        <ReviewsSection vendorId={vendor.id} />
      </div>

      {/* Sticky cart bar */}
      {count > 0 && (
        <div className="fixed bottom-16 md:bottom-4 left-0 right-0 z-40 px-4 md:px-8">
          <button
            data-testid="view-cart-bar"
            onClick={() => navigate("/cart")}
            className="max-w-6xl mx-auto w-full bg-primary text-white rounded-full h-13 py-3.5 px-6 flex items-center justify-between font-semibold shadow-xl hover:bg-primary/90 transition-colors"
          >
            <span className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" /> {count} item{count > 1 ? "s" : ""} in cart
            </span>
            <span>View cart →</span>
          </button>
        </div>
      )}
    </div>
  );
}

function EnquiryDialog({ vendor, open, setOpen }) {
  const [form, setForm] = useState({
    customer_name: "", phone: "", email: "", event_type: "", event_date: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.customer_name || !form.phone || !form.email || !form.event_type) {
      toast.error("Please fill name, phone, email and event type");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { vendor_id: vendor.id, ...form });
      toast.success("Enquiry sent! The vendor will reach out soon.");
      setOpen(false);
      setForm({ customer_name: "", phone: "", email: "", event_type: "", event_date: "", message: "" });
    } catch (e) {
      toast.error("Could not send enquiry. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="open-enquiry-btn"
          className="w-full md:w-auto flex items-center justify-center gap-2 border border-primary text-primary font-semibold px-5 h-11 rounded-full hover:bg-accent transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> Send an enquiry
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Enquire with {vendor.name}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Input data-testid="enq-name" placeholder="Your name" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <Input data-testid="enq-phone" placeholder="Phone" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input data-testid="enq-email" placeholder="Email" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="enq-event" placeholder="Event type (e.g. Marriage)" value={form.event_type}
            onChange={(e) => setForm({ ...form, event_type: e.target.value })} />
          <Input data-testid="enq-date" type="date" value={form.event_date}
            onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
          <Textarea data-testid="enq-message" placeholder="Tell us about your event..." value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button
            data-testid="submit-enquiry-btn"
            onClick={submit}
            disabled={submitting}
            className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Send enquiry"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stars({ value, size = "h-4 w-4" }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= value ? "fill-secondary text-secondary" : "text-stone-300"}`} />
      ))}
    </span>
  );
}

function ReviewsSection({ vendorId }) {
  const [open, setOpen] = useState(false);
  const { data: reviews = [], refetch } = useQuery({
    queryKey: ["reviews", vendorId],
    queryFn: async () => (await api.get(`/vendors/${vendorId}/reviews`)).data,
  });
  const avg = reviews.length
    ? Math.round((reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) * 10) / 10
    : 0;

  return (
    <section className="mt-10" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">Customer reviews</h2>
          {reviews.length > 0 ? (
            <div className="flex items-center gap-2 mt-1 text-sm">
              <Stars value={Math.round(avg)} />
              <span className="font-bold">{avg}</span>
              <span className="text-muted-foreground">· {reviews.length} review{reviews.length > 1 ? "s" : ""}</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">No reviews yet — be the first to share your experience.</p>
          )}
        </div>
        <button
          data-testid="open-review-btn"
          onClick={() => setOpen(true)}
          className="shrink-0 flex items-center gap-1.5 border border-primary text-primary font-semibold px-4 h-10 rounded-full hover:bg-accent transition-colors text-sm"
        >
          <PencilLine className="h-4 w-4" /> Write a review
        </button>
      </div>

      {reviews.length > 0 && (
        <div className="grid gap-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-card border border-stone-200 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{r.customer_name}</p>
                <Stars value={r.rating} />
              </div>
              {r.event_type ? <span className="overline text-primary">{r.event_type}</span> : null}
              {r.comment ? <p className="text-sm text-muted-foreground mt-1">{r.comment}</p> : null}
              <p className="text-[11px] text-stone-400 mt-2">{(r.created_at || "").slice(0, 10)}</p>
            </div>
          ))}
        </div>
      )}

      <WriteReviewDialog vendorId={vendorId} open={open} setOpen={setOpen} onDone={refetch} />
    </section>
  );
}

function WriteReviewDialog({ vendorId, open, setOpen, onDone }) {
  const [rating, setRating] = useState(5);
  const [form, setForm] = useState({ customer_name: "", event_type: "", comment: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Please add your name");
    if (!rating) return toast.error("Please pick a star rating");
    setSubmitting(true);
    try {
      await api.post(`/vendors/${vendorId}/reviews`, { ...form, rating });
      toast.success("Thanks! Your review is posted.");
      setOpen(false);
      setForm({ customer_name: "", event_type: "", comment: "" });
      setRating(5);
      onDone();
    } catch {
      toast.error("Could not post your review. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Write a review</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="flex items-center gap-1" data-testid="review-star-input">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`star-${n}`}
                onClick={() => setRating(n)}
                className="p-0.5"
              >
                <Star className={`h-8 w-8 transition-colors ${n <= rating ? "fill-secondary text-secondary" : "text-stone-300"}`} />
              </button>
            ))}
          </div>
          <Input data-testid="review-name" placeholder="Your name" value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          <Input data-testid="review-event" placeholder="Event type (optional, e.g. Marriage)" value={form.event_type}
            onChange={(e) => setForm({ ...form, event_type: e.target.value })} />
          <Textarea data-testid="review-comment" placeholder="How was your experience?" value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          <button
            data-testid="submit-review-btn"
            onClick={submit}
            disabled={submitting}
            className="h-11 bg-primary text-white rounded-full font-semibold disabled:opacity-60"
          >
            {submitting ? "Posting..." : "Post review"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
